// src/components/AiChart/AiChart.jsx

import React, { useMemo } from "react";
import {
    Bar,
    BarChart,
    CartesianGrid,
    Cell,
    Pie,
    PieChart,
    ResponsiveContainer,
    Tooltip,
    XAxis,
    YAxis,
} from "recharts";

const COLORS = [
    "#6B5CA5",
    "#FF9AA2",
    "#9AD0F5",
    "#B39DDB",
    "#FFCC80",
    "#90CAF9",
    "#CE93D8",
    "#A5D6A7",
    "#FFE082",
    "#80CBC4",
];

function normalize(s) {
    return String(s ?? "").trim();
}

function includesAllTokens(line, tokens) {
    const t = normalize(line).toLowerCase();
    return (tokens || []).every((tk) => t.includes(normalize(tk).toLowerCase()));
}

function isSeparatorRow(line) {
    const t = normalize(line);
    return t.includes("|") && /-+/.test(t);
}

function isTableRow(line) {
    const t = normalize(line);
    return t.includes("|");
}

function parseRowCells(line) {
    const trimmed = normalize(line);
    const core = trimmed.startsWith("|") ? trimmed.slice(1) : trimmed;
    const core2 = core.endsWith("|") ? core.slice(0, -1) : core;
    return core2.split("|").map((c) => normalize(c));
}

function findTableBlockByHeaderTokens(markdown, headerTokens) {
    if (!markdown) return null;
    const lines = String(markdown).split("\n");

    for (let i = 0; i < lines.length; i++) {
        const line = lines[i];
        if (!includesAllTokens(line, headerTokens)) continue;

        const sep = lines[i + 1];
        if (!sep || !isSeparatorRow(sep)) continue;

        const tableLines = [line, sep];
        let j = i + 2;

        while (j < lines.length) {
            const row = lines[j];
            if (!isTableRow(row)) break;
            tableLines.push(row);
            j++;
        }

        if (tableLines.length >= 3) {
            return tableLines.join("\n");
        }
    }

    return null;
}

export function parseChartDataFromTable(tableBlock, headerTokens) {
    if (!tableBlock) return [];

    const lines = tableBlock.split("\n").filter((l) => normalize(l).length > 0);
    if (lines.length < 3) return [];

    const headerCells = parseRowCells(lines[0]);

    const nameToken = normalize(headerTokens?.[0] ?? "");
    const valueToken = normalize(headerTokens?.[1] ?? "");

    const idxName = headerCells.findIndex((c) => c.includes(nameToken));
    const idxValue = headerCells.findIndex((c) => c.includes(valueToken));

    const rows = lines.slice(2);
    const data = [];

    for (const r of rows) {
        const cells = parseRowCells(r);
        const name = idxName >= 0 ? cells[idxName] : "";
        const valueRaw = idxValue >= 0 ? cells[idxValue] : "";
        const value = Number(String(valueRaw).replace(/[^0-9]/g, ""));

        if (!name) continue;
        if (!Number.isFinite(value)) continue;

        data.push({ name, value });
    }

    return data;
}

function ChartLegend({ data, showValue = true }) {
    if (!data || data.length === 0) return null;

    return (
        <div className="mt-3 flex flex-wrap gap-2">
            {data.map((item, idx) => (
                <div
                    key={`${item.name}-${idx}`}
                    className="flex items-center gap-2 rounded-[4px] border border-[rgba(0,0,0,0.06)] bg-[#f3f6fb] px-2 py-1 text-[12px]"
                >
                    <span
                        className="inline-block h-2.5 w-2.5 rounded-[2px]"
                        style={{ backgroundColor: COLORS[idx % COLORS.length] }}
                    />
                    <span className="text-[#111827]">
                        {item.name}
                        {showValue ? `(${item.value})` : ""}
                    </span>
                </div>
            ))}
        </div>
    );
}

function ChartCard({ title, children, colSpan2 = false }) {
    return (
        <div
            className={[
                "chart-card min-w-0 rounded-[6px] border border-[rgba(0,0,0,0.06)] bg-white p-4 mb-5",
                colSpan2 ? "col-span-2" : "",
            ].join(" ")}
        >
            <div className="chart-title mb-3 text-[16px] font-semibold text-[#111827]">{title}</div>
            {children}
        </div>
    );
}

function inferKind(cfg) {
    const t = normalize(cfg?.title).toLowerCase();
    if (cfg?.kind) return cfg.kind;
    if (t.includes("pie")) return "pie";
    if (t.includes("bar")) return "bar";
    return "pie";
}

function ChartTooltip({ active, payload, label, valueLabel }) {
    if (!active || !Array.isArray(payload) || payload.length === 0) return null;

    const first = payload[0] || {};
    const row = first.payload || {};
    const code = row.name ?? first.name ?? label ?? "-";
    const value = row.value ?? first.value ?? "-";

    return (
        <div className="rounded-[6px] border border-[rgba(0,0,0,0.08)] bg-white px-3 py-2 text-[12px] shadow-sm">
            <div className="text-[#111827]">코드 : {code}</div>
            <div className="text-[#111827]">{valueLabel || "건수"} : {value}</div>
        </div>
    );
}

export function InlineChartCard({ kind = "pie", title, data, valueLabel, colSpan2 = false }) {
    if (!Array.isArray(data) || data.length === 0) {
        return (
            <ChartCard title={title || "Chart"} colSpan2={colSpan2}>
                <div className="text-[12px] text-[#6b7280]">차트 데이터가 없습니다.</div>
            </ChartCard>
        );
    }

    const safeValueLabel = valueLabel || "건수";

    if (kind === "bar") {
        return (
            <ChartCard title={title || "Bar"} colSpan2={colSpan2}>
                <div className="flex flex-col">
                    <ResponsiveContainer width="100%" height={160}>
                        <BarChart data={data}>
                            <CartesianGrid strokeDasharray="3 3" />
                            <XAxis dataKey="name" hide />
                            <YAxis />
                            <Tooltip content={<ChartTooltip valueLabel={safeValueLabel} />} />
                            <Bar dataKey="value">
                                {data.map((_, i) => (
                                    <Cell key={`cell-${i}`} fill={COLORS[i % COLORS.length]} />
                                ))}
                            </Bar>
                        </BarChart>
                    </ResponsiveContainer>
                    <ChartLegend data={data} />
                </div>
            </ChartCard>
        );
    }

    return (
        <ChartCard title={title || "Pie"} colSpan2={colSpan2}>
            <div className="flex flex-col">
                <ResponsiveContainer width="100%" height={160}>
                    <PieChart>
                        <Tooltip content={<ChartTooltip valueLabel={safeValueLabel} />} />
                        <Pie
                            data={data}
                            dataKey="value"
                            nameKey="name"
                            cx="50%"
                            cy="50%"
                            outerRadius={62}
                            label
                        >
                            {data.map((_, i) => (
                                <Cell key={`cell-${i}`} fill={COLORS[i % COLORS.length]} />
                            ))}
                        </Pie>
                    </PieChart>
                </ResponsiveContainer>
                <ChartLegend data={data} />
            </div>
        </ChartCard>
    );
}

export default function ChartsPanel({ markdown, chartConfigs }) {
    const configs = Array.isArray(chartConfigs) ? chartConfigs : [];

    const tableCache = useMemo(() => {
        const map = new Map();

        for (const cfg of configs) {
            const tokensKey = (cfg?.headerTokens || []).map(normalize).join("|");
            const uniqueKey = `${cfg?.registryKey || ""}:${cfg?.datasetKey || ""}:${cfg?.kind || ""}:${tokensKey}`;

            if (map.has(uniqueKey)) continue;

            const tableBlock = findTableBlockByHeaderTokens(markdown, cfg.headerTokens || []);
            const data = parseChartDataFromTable(tableBlock, cfg.headerTokens || []);
            map.set(uniqueKey, { tableBlock, data });
        }

        return map;
    }, [markdown, configs]);

    if (!configs.length) return null;

    const onlyOne = configs.length === 1;

    return (
        <div className="charts-grid mb-4 grid grid-cols-2 gap-4">
            {configs.map((cfg, idx) => {
                const kind = inferKind(cfg);
                const tokensKey = (cfg?.headerTokens || []).map(normalize).join("|");
                const uniqueKey = `${cfg?.registryKey || ""}:${cfg?.datasetKey || ""}:${cfg?.kind || ""}:${tokensKey}`;
                const cached = tableCache.get(uniqueKey) || {};

                return (
                    <InlineChartCard
                        key={idx}
                        kind={kind}
                        title={cfg?.title}
                        data={cached.data || []}
                        valueLabel={cfg?.valueLabel}
                        colSpan2={onlyOne}
                    />
                );
            })}
        </div>
    );
}
